import React from "react";
import tw from "twin.macro";
import { motion } from "framer-motion";
import styled from "styled-components";
import { PrimaryLink as PrimaryLinkBase } from "components/misc/Links.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import { ReactComponent as LocationIcon } from "feather-icons/dist/icons/map-pin.svg";
import { ReactComponent as TimeIcon } from "feather-icons/dist/icons/clock.svg";
import { ReactComponent as ArrowRightIcon } from "images/arrow-right-icon.svg";
import { useTranslation } from "react-i18next";
import newGradBgImg from "images/recruitment/new-grad-recruitment.jpg";
import midBgImg from "images/recruitment/mid-level-recruitment.png";
import BgTitle from "components/title/BgTitle";
import RecruitmentCardMobileView from "./RecruitmentCardMobileView";
const BgWrapper = tw.div`fancy-feature-mask`;

const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24`;

const ThreeColumn = tw.div`flex flex-wrap`;
const Column = tw.div`xl:mr-12 xl:last:mr-0`;
const HeadingColumn = styled(Column)((props) => [
  tw`w-full xl:w-5/12`,
  props.textOnLeft ? tw`xl:order-first` : tw`xl:order-last xl:ml-12 xl:mr-0`,
]);
const CardColumn = tw(
  Column
)`w-full md:w-1/2 xl:w-3/12 mt-16 xl:mt-0 xl:last:ml-auto`;

const HeadingInfoContainer = tw.div`text-center xl:text-left max-w-lg xl:max-w-none mx-auto xl:mx-0`;
const HeadingDescription = tw.p`text-sm leading-relaxed mt-8`;
const PrimaryLink = styled(PrimaryLinkBase)`
  ${tw`inline-flex text-ncc justify-center xl:justify-start items-center mt-8 text-sm`}
  svg {
    ${tw`ml-2 w-5 h-5`}
  }
`;

const Card = styled.div`
  ${tw`mx-auto xl:mx-0 xl:ml-auto max-w-sm md:max-w-md lg:max-w-md xl:max-w-md p-2 rounded-md shadow-sm`}
  background-color: #f8f9fa; /* Light grey background color */
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid #0c4d96;
  &:hover {
    background-color: #e9ecef; /* Slightly darker grey on hover */
    box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.15); /* Add a subtle shadow */
  }
`;

const CardImageContainer = styled.div`
  ${tw`relative overflow-hidden`}
  img {
    ${tw`w-full h-40 object-cover transform transition-transform duration-500`}
  }
  &:hover img {
    transform: scale(1.1);
  }
`;

const CardText = tw.div`p-6`;
const CardHeader = tw.div`flex justify-between items-center mb-4`;
const CardCompany = tw.h5`font-bold text-gray-900 text-sm`;
const CardType = tw.span`text-ncc font-medium text-sm`;

const CardTitle = tw.h6`font-semibold text-gray-800 text-base mb-2`;
const CardMeta = tw.div`flex justify-between items-center text-gray-500 text-xs mt-auto pt-4 border-t border-gray-200`;
const CardMetaFeature = styled.div`
  ${tw`flex items-center`}
  svg {
    ${tw`w-4 h-4 mr-1`}
  }
`;

const CardAction = styled(PrimaryButtonBase)`
  ${tw`w-full text-sm mt-4 bg-ncc text-white transition-all`}
`;

const images = [newGradBgImg, midBgImg];

export default ({ isXs = false }) => {
  if (isXs) return <RecruitmentCardMobileView />;
  const { t } = useTranslation();

  const subheading = t("recruitment.subheading");
  const description = (
    <>
      <CardType>{t("recruitment.description.subtitle")}</CardType>
      <CardTitle>{t("recruitment.description.contact.title")}</CardTitle>
      <CardType>{t("recruitment.description.contact.details")}</CardType>
    </>
  );
  const linkText = t("recruitment.linkText");
  const cardLinkText = t("recruitment.linkText");
  const cards = t("recruitment.cards", { returnObjects: true });

  return (
    <BgWrapper>
      <Container>
        <Content>
          <ThreeColumn>
            <HeadingColumn textOnLeft={false}>
              <HeadingInfoContainer>
                <BgTitle text={subheading} />
                <HeadingDescription>{description}</HeadingDescription>
                <a
                  href="https://www.unics-holdings.co.jp/saiyo/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PrimaryLink>
                    {linkText} <ArrowRightIcon />
                  </PrimaryLink>
                </a>
              </HeadingInfoContainer>
            </HeadingColumn>
            {cards.map((card, index) => (
              <CardColumn key={index}>
                <Card
                  whileHover={{
                    y: -5,
                    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.15)",
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <CardImageContainer>
                    <motion.img
                      src={images[index]}
                      alt={card.title}
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </CardImageContainer>
                  <CardText>
                    <CardHeader>
                      <CardCompany>{card.company}</CardCompany>
                      <CardType>{card.type}</CardType>
                    </CardHeader>
                    <CardTitle>{card.title}</CardTitle>
                    <CardMeta>
                      <CardMetaFeature>
                        <TimeIcon /> {card.durationText}
                      </CardMetaFeature>
                      <CardMetaFeature>
                        <LocationIcon /> {card.locationText}
                      </CardMetaFeature>
                    </CardMeta>
                    {index === 0 ? (
                      <a
                        href={card.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Apply for ${card.title}`}
                      >
                        <CardAction>{cardLinkText}</CardAction>
                      </a>
                    ) : (
                      <a
                        href={`mailto:${t("footer_contact_email_soumu")}`}
                        aria-label="Contact via email"
                        className="card-link"
                      >
                        <CardAction>{t("header_nav_contactus")}</CardAction>
                      </a>
                    )}
                  </CardText>
                </Card>
              </CardColumn>
            ))}
          </ThreeColumn>
        </Content>
      </Container>
    </BgWrapper>
  );
};
